import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useBrand } from "../lib/useBrand";
import CompanyWorkspaceNav from "./CompanyWorkspaceNav";
import {
  LayoutDashboard,
  Building2,
  LogOut,
  ShieldCheck,
  Settings as SettingsIcon,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/companies", label: "Client Companies", icon: Building2, testid: "nav-companies" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const brand = useBrand();
  const brandName = brand.name || "POSTINGS INC";
  const nav = NAV.filter((n) => (!n.adminOnly || user?.role === "super_admin") && (!n.masterOnly || (user?.staff_role || "master") === "master"));
  const [companies, setCompanies] = useState([]);
  const [collapsed, setCollapsed] = useState(() => (typeof window !== "undefined" && localStorage.getItem("sidebar_collapsed") === "1"));

  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem("sidebar_collapsed", next ? "1" : "0");
    return next;
  });

  useEffect(() => {
    if (user?.role === "super_admin") api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, [user]);

  return (
    <div className="admin-theme min-h-screen flex bg-[#fafafa]">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" data-collapsed={collapsed} className={`hidden md:flex ${collapsed ? "w-[72px]" : "w-64"} flex-col bg-[#0a0a0a] text-white sticky top-0 h-screen border-r border-black transition-[width] duration-300 ease-in-out`}>
        <div className={`flex items-center ${collapsed ? "justify-center px-2" : "justify-between px-6"} py-6 border-b border-white/10`}>
          {collapsed ? (
            brand.logo ? (
              <img src={brand.logo} alt={brandName} data-testid="sidebar-brand-logo" className="h-9 w-9 object-contain bg-white p-1" />
            ) : (
              <div className="h-8 w-8 bg-white text-black flex items-center justify-center font-black text-sm">P</div>
            )
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              {brand.logo ? (
                <img src={brand.logo} alt={brandName} data-testid="sidebar-brand-logo" className={`h-14 w-auto object-contain bg-white p-1.5 ${brand.name ? "max-w-[120px]" : "max-w-full"}`} />
              ) : (
                <div className="h-7 w-7 bg-white text-black flex items-center justify-center font-black text-sm">P</div>
              )}
              {(brand.name || !brand.logo) && (
                <div className="min-w-0">
                  <div className="font-black text-sm leading-none tracking-tight truncate" data-testid="sidebar-brand-name">{brandName}</div>
                  <div className="text-[10px] text-white/50 font-mono mt-1 uppercase tracking-widest">Payroll Ops</div>
                </div>
              )}
            </div>
          )}
          {!collapsed && (
            <button data-testid="sidebar-collapse-toggle" onClick={toggleCollapsed} title="Collapse sidebar" className="text-white/50 hover:text-white transition-colors ml-2 flex-shrink-0">
              <PanelLeftClose className="h-5 w-5" />
            </button>
          )}
        </div>
        {collapsed && (
          <button data-testid="sidebar-expand-toggle" onClick={toggleCollapsed} title="Expand sidebar" className="mx-auto mt-3 mb-1 text-white/50 hover:text-white transition-colors">
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        <nav className="flex-1 py-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              title={collapsed ? n.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed ? "justify-center px-0" : "px-6"} py-3 text-sm font-medium transition-colors duration-200 border-l-2 ${
                  isActive
                    ? "bg-white text-black border-white"
                    : "text-white/60 border-transparent hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
              {!collapsed && n.label}
            </NavLink>
          ))}
        </nav>
        {!collapsed && (
          <div className="px-6 py-4 border-t border-white/10 text-[10px] font-mono text-white/40 uppercase tracking-widest">
            CRA T4127 · Jan 2026 · ON
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <div className="flex items-center gap-2 md:hidden">
              {brand.logo ? (
                <img src={brand.logo} alt={brandName} className="h-6 w-auto max-w-[110px] object-contain" />
              ) : (
                <div className="h-6 w-6 bg-black text-white flex items-center justify-center font-black text-xs">P</div>
              )}
              {(brand.name || !brand.logo) && (
                <span className="font-black text-sm tracking-tight">{brandName}</span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-widest">
              <ShieldCheck className="h-3.5 w-3.5" /> Internal Payroll Service
            </div>
            <div className="flex items-center gap-4">
              {user?.role === "super_admin" && companies.length > 0 && (
                <select
                  data-testid="company-switcher"
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) navigate(`/companies/${e.target.value}`); }}
                  className="hidden sm:block border border-input bg-white px-3 py-2 text-sm max-w-[200px] focus:outline-none focus:ring-2 focus:ring-black"
                  title="Jump to a client company"
                >
                  <option value="">Jump to company…</option>
                  {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              )}
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium leading-none" data-testid="header-user-name">{user?.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                  {user?.role === "super_admin" ? "Admin" : "Client"}
                </div>
              </div>
              <div className="h-9 w-9 bg-black text-white flex items-center justify-center font-bold text-sm">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <button
                data-testid="logout-button"
                onClick={logout}
                className="h-9 w-9 flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors duration-200"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Mobile nav */}
          <nav className="md:hidden flex overflow-x-auto border-t border-border bg-white">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex-shrink-0 px-4 py-2.5 text-xs font-medium whitespace-nowrap ${
                    isActive ? "bg-black text-white" : "text-muted-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="relative flex-1 p-4 md:p-8 fade-up">
          {(brand.login_logo || brand.logo) && (
            <img
              src={brand.login_logo || brand.logo}
              alt={brandName}
              data-testid="dashboard-brand-logo"
              className="pointer-events-none select-none absolute top-4 right-4 md:top-8 md:right-8 h-10 md:h-12 w-auto max-w-[180px] object-contain"
            />
          )}
          <CompanyWorkspaceNav />{children}
        </main>
      </div>
    </div>
  );
}
